import { computeWordDurations } from '@src/editor/services/karaokeWordTiming';

import {
  placeableSegments,
  recordingSecToClipSec,
} from './DubPlacementService';

import type { ClipSpan } from './DubPlacementService';
import type {
  DubManifestDTO,
  DubManifestSegment,
  DubWordMark,
} from '@src/repositories/DubbingRepository';

/**
 * The dub's words, timed to the dubbed voice (PLAT-10488).
 *
 * Pure: no editor, no MobX, no network. It is handed a dub manifest and the
 * signed rendition addresses, and returns each dubbed line with its words on the
 * recording's clock; `placeTimelineOnClip` then puts those words on one clip's
 * own clock through the clip's kept stretches.
 *
 * - **The stored `words[]` IS the word list.** Nothing here splits a line into
 *   words or re-attaches punctuation; the backend's splitter is the only place
 *   that decides where a word ends. A reader never re-splits
 *   `sourceTranslationSnapshot`.
 * - **The line's text is rebuilt exactly as the contract writes it**
 *   (`joinDubWords`), so "iPhone 15 Pro" inside Japanese keeps its spaces.
 * - **The same lines as the audio.** Lines come from `placeableSegments`, so a
 *   superseded copy of a line, or a line with no signed address, is never shown
 *   because it is never heard.
 * - **The same arithmetic as the audio.** A word is placed on the clip clock by
 *   the audio's own span maths, so it is shown where its sound plays.
 */

/** Where a line plays on the story timeline. Older manifests carry no
 * `placedStartMs` and fall back to the original start. */
export function segmentStartMs(
  segment: Pick<DubManifestSegment, 'placedStartMs' | 'originalStartMs'>,
): number {
  return segment.placedStartMs ?? segment.originalStartMs;
}

/**
 * Where the dub's last line ends on the story timeline: how long the voice
 * runs when every line is laid at its own start from the story's start.
 */
export function dubEndMs(
  segments: readonly Pick<
    DubManifestSegment,
    'placedStartMs' | 'originalStartMs' | 'renditionDurationMs'
  >[],
): number {
  return segments.reduce(
    (max, segment) =>
      Math.max(max, segmentStartMs(segment) + segment.renditionDurationMs),
    0,
  );
}

/**
 * Rebuild a line's text from its stored words — the contract's one rule, the
 * same in both repositories, with no language test:
 *
 * - no word carries `spaceAfter` → an OLD line, split on whitespace before the
 *   field existed: the words are joined with single spaces;
 * - otherwise → a NEW line: each word is followed by exactly the whitespace
 *   written after it (`''` when nothing follows, as between most Chinese or
 *   Japanese words).
 *
 * An empty word list rebuilds as `''`.
 */
export function joinDubWords(
  words: readonly Pick<DubWordMark, 'text' | 'spaceAfter'>[],
): string {
  if (!hasSpacingField(words)) return words.map(w => w.text).join(' ');
  return words.map(w => w.text + (w.spaceAfter ?? '')).join('');
}

function hasSpacingField(
  words: readonly Pick<DubWordMark, 'spaceAfter'>[],
): boolean {
  return words.some(w => typeof w.spaceAfter === 'string');
}

/** One word of a dubbed line, timed on the recording's clock. */
export interface SpokenWord {
  text: string;
  startMs: number;
  endMs: number;
  /** Where the word sits in its line's `spokenText`, `[charStart, charEnd)`. */
  charStart: number;
  charEnd: number;
}

/** One dubbed line, as the voice says it. */
export interface TimelineSegment {
  segmentId: string;
  speaker: string;
  /** Where the line's audio starts and ends on the recording's clock. */
  startMs: number;
  endMs: number;
  /** The line's text rebuilt from its words (or its snapshot, see `wordsKnown`). */
  spokenText: string;
  words: SpokenWord[];
  /** Does the rebuilt text equal the text the line was recorded from? */
  snapshotMatches: boolean;
  /**
   * False when the manifest stored no words for the line. The line is then ONE
   * unit — its snapshot text over its whole span — which is the estimate a
   * translated phrase gets today.
   */
  wordsKnown: boolean;
}

/** Collapse every whitespace run to one space, trimmed. */
function collapseSpaces(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function timeLine(segment: DubManifestSegment): TimelineSegment {
  const startMs = segmentStartMs(segment);
  const endMs = startMs + segment.renditionDurationMs;
  const snapshot = segment.sourceTranslationSnapshot ?? '';
  const stored = Array.isArray(segment.words) ? segment.words : [];

  if (stored.length === 0) {
    const spokenText = snapshot.trim();
    return {
      segmentId: segment.segmentId,
      speaker: segment.speaker,
      startMs,
      endMs,
      spokenText,
      words:
        spokenText.length > 0
          ? [
              {
                text: spokenText,
                startMs,
                endMs,
                charStart: 0,
                charEnd: spokenText.length,
              },
            ]
          : [],
      snapshotMatches: false,
      wordsKnown: false,
    };
  }

  const newLine = hasSpacingField(stored);
  const spokenText = joinDubWords(stored);

  // Each word's place in the rebuilt text — the same walk `joinDubWords` makes.
  const offsets: { charStart: number; charEnd: number }[] = [];
  let cursor = 0;
  stored.forEach((word, i) => {
    offsets.push({ charStart: cursor, charEnd: cursor + word.text.length });
    cursor += word.text.length;
    if (newLine) cursor += (word.spaceAfter ?? '').length;
    else if (i < stored.length - 1) cursor += 1;
  });

  // An estimated line keeps its words and their text; only its times are
  // spread again over the line by the words' visual width, the same estimate a
  // translated phrase gets today.
  const estimated = segment.timing?.source === 'estimated';
  const estimatedDurations = estimated
    ? computeWordDurations(
        stored.map(w => w.text),
        endMs - startMs,
        'character-weighted',
      )
    : [];

  let estimateCursor = startMs;
  const words: SpokenWord[] = stored.map((word, i) => {
    let wordStart: number;
    let wordEnd: number;
    if (estimated) {
      wordStart = estimateCursor;
      wordEnd = estimateCursor + estimatedDurations[i];
      estimateCursor = wordEnd;
    } else {
      // Marks are relative to the line's own start; a mark running past the
      // line's audio is clamped to it.
      wordStart = startMs + word.offsetMs;
      wordEnd = wordStart + word.durationMs;
    }
    const clampedStart = Math.min(Math.max(wordStart, startMs), endMs);
    const clampedEnd = Math.min(Math.max(wordEnd, clampedStart), endMs);
    return {
      text: word.text,
      startMs: clampedStart,
      endMs: clampedEnd,
      ...offsets[i],
    };
  });

  const snapshotMatches = newLine
    ? spokenText === snapshot.trimStart()
    : collapseSpaces(spokenText) === collapseSpaces(snapshot);

  return {
    segmentId: segment.segmentId,
    speaker: segment.speaker,
    startMs,
    endMs,
    spokenText,
    words,
    snapshotMatches,
    wordsKnown: true,
  };
}

/**
 * Every line the dub's audio places, in story-timeline order, with its words
 * timed on the recording's clock.
 */
export function buildDubWordTimeline(
  manifest: DubManifestDTO,
  renditionUrls: Record<string, string>,
): TimelineSegment[] {
  if (!manifest || !Array.isArray(manifest.segments)) return [];
  return placeableSegments(manifest, renditionUrls).live.map(timeLine);
}

/** One shown word, on the clip's own clock. */
export interface ClipWord {
  segmentId: string;
  charStart: number;
  charEnd: number;
  text: string;
  startSec: number;
  endSec: number;
  /** The kept stretch (index into the clip's spans) the word is shown in. */
  stretchIndex: number;
}

/**
 * Put each line's words on one clip's clock. A word is shown when its middle
 * lies inside a kept stretch — the clip cuts it where it cuts the audio — and
 * its span is clamped to that stretch. Lines with no shown word are absent.
 */
export function placeTimelineOnClip(
  segments: readonly TimelineSegment[],
  spans: readonly ClipSpan[],
): Map<string, ClipWord[]> {
  const placed = new Map<string, ClipWord[]>();
  for (const segment of segments) {
    const shown: ClipWord[] = [];
    for (const word of segment.words) {
      const startSec = word.startMs / 1000;
      const endSec = word.endMs / 1000;
      const middleSec = (startSec + endSec) / 2;
      const stretchIndex = spans.findIndex(
        span =>
          middleSec >= span.sourceStartSec &&
          middleSec < span.sourceStartSec + span.durationSec,
      );
      if (stretchIndex < 0) continue;
      const span = spans[stretchIndex];
      const clampedStart = Math.max(startSec, span.sourceStartSec);
      const clampedEnd = Math.min(
        endSec,
        span.sourceStartSec + span.durationSec,
      );
      shown.push({
        segmentId: segment.segmentId,
        charStart: word.charStart,
        charEnd: word.charEnd,
        text: word.text,
        startSec: recordingSecToClipSec(clampedStart, span),
        endSec: recordingSecToClipSec(clampedEnd, span),
        stretchIndex,
      });
    }
    if (shown.length > 0) placed.set(segment.segmentId, shown);
  }
  return placed;
}

/**
 * A dub line on the clip's clock: from where its recording starts playing to
 * where it ends, so the silence a recording begins or ends with belongs to the
 * line (PLAT-11906: the playing line is found from `placedStartMs` and
 * `renditionDurationMs`, not from its words).
 */
export interface DubLineOnClip {
  segmentId: string;
  startSec: number;
  endSec: number;
  wordsKnown: boolean;
}

/**
 * Where a listener is in the dub at `t`, on the clip's clock: the line whose
 * recording is playing then and, in it, the word most recently started — a
 * word counts as reached the moment it starts, because it is read while it is
 * heard, and it stays reached through the pauses after it. The line alone
 * while none of its words has started yet, or when the manifest stored none.
 * `null` between lines, before the first and after the last.
 */
export function locateDubWord(
  words: ReadonlyMap<string, readonly ClipWord[]>,
  lines: readonly DubLineOnClip[],
  t: number,
):
  | { segmentId: string; charStart: number; charEnd: number }
  | { segmentId: string }
  | null {
  const line = lines.find(
    candidate => t >= candidate.startSec && t < candidate.endSec,
  );
  if (!line) return null;
  const started = (words.get(line.segmentId) ?? []).filter(
    word => word.startSec <= t,
  );
  if (!line.wordsKnown || started.length === 0) {
    return { segmentId: line.segmentId };
  }
  const reached = started.reduce((latest, word) =>
    word.startSec >= latest.startSec ? word : latest,
  );
  return {
    segmentId: line.segmentId,
    charStart: reached.charStart,
    charEnd: reached.charEnd,
  };
}
